import { createSvgEl } from './svg';

export interface BipartiteNode {
  id: string;
  label: string;
  className?: string;
}

export interface BipartiteArrow {
  from: string;
  to: string;
  className?: string;
}

export interface BipartiteOptions {
  left: readonly BipartiteNode[];
  right: readonly BipartiteNode[];
  arrows: readonly BipartiteArrow[];
  onToggleArrow?(from: string, to: string): void;
}

export interface BipartiteHandle {
  group: SVGGElement;
  update(opts: Partial<BipartiteOptions>): void;
  destroy(): void;
}

// Layout constants — no inline colours anywhere.
const NODE_R = 18;
const COL_LEFT_X = 80;
const COL_RIGHT_X = 280;
const ROW_START_Y = 60;
const ROW_STEP = 70;
const MARKER_ID = 'bp-arrow-marker';

/** Vertical y-centre for a node at position index in its column. */
function nodeY(index: number): number {
  return ROW_START_Y + index * ROW_STEP;
}

/**
 * Builds an SVG arrow-diagram (bipartite graph) showing two columns of labelled
 * nodes connected by directed arrows.
 *
 * - Nodes are labelled circles; `node.className` is applied when present.
 * - Arrows are `<line>` elements with `data-arrow` attribute and a marker-end
 *   arrowhead; `arrow.className` is applied when present.
 * - When `onToggleArrow` is provided, an invisible clickable rect is rendered for
 *   every (left, right) pair carrying `data-cell="${from}:${to}"`.
 * - All colours come from CSS custom properties — no inline colour values.
 */
export function createBipartite(
  parent: SVGSVGElement | SVGGElement,
  opts: BipartiteOptions,
): BipartiteHandle {
  let current: BipartiteOptions = { ...opts };

  // Outer <g> is the returned handle.group.
  const group = createSvgEl('g', { class: 'bp-bipartite' });
  parent.appendChild(group);

  // Ensure arrowhead marker exists in the nearest <svg> ancestor's <defs>.
  ensureMarker(parent);

  function render(): void {
    group.replaceChildren();

    const leftMap = new Map(current.left.map((n, i) => [n.id, { node: n, y: nodeY(i) }]));
    const rightMap = new Map(current.right.map((n, i) => [n.id, { node: n, y: nodeY(i) }]));

    // Invisible clickable cells (left × right) — rendered first so arrows sit on top.
    if (current.onToggleArrow) {
      for (const ln of current.left) {
        const lEntry = leftMap.get(ln.id);
        if (!lEntry) continue;
        for (const rn of current.right) {
          const rEntry = rightMap.get(rn.id);
          if (!rEntry) continue;
          const cellY = Math.min(lEntry.y, rEntry.y) - NODE_R;
          const cellH = Math.abs(lEntry.y - rEntry.y) + NODE_R * 2;
          const cell = createSvgEl('rect', {
            class: 'bp-cell',
            'data-cell': `${ln.id}:${rn.id}`,
            x: COL_LEFT_X + NODE_R,
            y: cellY,
            width: COL_RIGHT_X - COL_LEFT_X - NODE_R * 2,
            height: cellH,
            opacity: '0',
          });
          const fromId = ln.id;
          const toId = rn.id;
          cell.addEventListener('click', () => current.onToggleArrow?.(fromId, toId));
          group.appendChild(cell);
        }
      }
    }

    // Arrows.
    for (const arrow of current.arrows) {
      const lEntry = leftMap.get(arrow.from);
      const rEntry = rightMap.get(arrow.to);
      if (!lEntry || !rEntry) continue;

      // Shorten the line so it meets the node circles, not their centres.
      const dx = COL_RIGHT_X - COL_LEFT_X;
      const dy = rEntry.y - lEntry.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      const ux = dx / len;
      const uy = dy / len;

      const x1 = COL_LEFT_X + ux * NODE_R;
      const y1 = lEntry.y + uy * NODE_R;
      const x2 = COL_RIGHT_X - ux * NODE_R;
      const y2 = rEntry.y - uy * NODE_R;

      const line = createSvgEl('line', {
        class: arrow.className ? `bp-arrow ${arrow.className}` : 'bp-arrow',
        'data-arrow': `${arrow.from}:${arrow.to}`,
        x1,
        y1,
        x2,
        y2,
        'marker-end': `url(#${MARKER_ID})`,
      });
      group.appendChild(line);
    }

    // Left-column nodes.
    for (const [, { node, y }] of leftMap) {
      appendNode(group, COL_LEFT_X, y, node);
    }

    // Right-column nodes.
    for (const [, { node, y }] of rightMap) {
      appendNode(group, COL_RIGHT_X, y, node);
    }
  }

  render();

  return {
    group,

    update(partial: Partial<BipartiteOptions>): void {
      current = { ...current, ...partial };
      render();
    },

    destroy(): void {
      group.remove();
    },
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Append a labelled circle node to `parent`. */
function appendNode(parent: SVGGElement, cx: number, cy: number, node: BipartiteNode): void {
  const g = createSvgEl('g', {
    class: node.className ? `bp-node ${node.className}` : 'bp-node',
    'data-node': node.id,
  });

  const circle = createSvgEl('circle', {
    class: 'bp-node-circle',
    cx,
    cy,
    r: NODE_R,
  });

  const text = createSvgEl('text', {
    class: 'bp-node-label',
    x: cx,
    y: cy,
    'text-anchor': 'middle',
    'dominant-baseline': 'central',
  });
  text.textContent = node.label;

  g.appendChild(circle);
  g.appendChild(text);
  parent.appendChild(g);
}

/**
 * Ensure a `<defs>` arrowhead marker with id `MARKER_ID` exists in the nearest
 * ancestor `<svg>` element. Idempotent — only inserts once per svg.
 */
function ensureMarker(el: SVGSVGElement | SVGGElement): void {
  // Walk up to find the <svg> root.
  let root: Element = el;
  while (root.parentElement && root.tagName !== 'svg') {
    root = root.parentElement;
  }
  if (root.querySelector(`#${MARKER_ID}`)) return;

  let defs = root.querySelector('defs');
  if (!defs) {
    defs = createSvgEl('defs');
    root.prepend(defs);
  }

  const marker = createSvgEl('marker', {
    id: MARKER_ID,
    class: 'bp-arrow-marker',
    markerWidth: '8',
    markerHeight: '8',
    refX: '6',
    refY: '3',
    orient: 'auto',
    markerUnits: 'strokeWidth',
  });

  const path = createSvgEl('path', {
    class: 'bp-arrow-marker-path',
    d: 'M0,0 L0,6 L6,3 z',
  });

  marker.appendChild(path);
  defs.appendChild(marker);
}
